<template>
  <div>

    <StatusLinks id="region-status-links" :statusWiseRecords="statusWiseRecords" @click="updateSearchResults" />

    <VForm as="div" class="columns is-mobile" v-slot="{ errors }">
      
      <!-- SEARCH -->
      <div id="region-sidebar" class="column is-one-third">
        
        <h2 class="title list-page-heading">Search</h2>
        
        <!-- Container -->
        <TogglePanel heading="Container" id="box-search-container" expanded>
          <TextInput 
            id="containerName" 
            v-model="containerName" 
            label="Name" 
            @input="debouncedSearch" 
            class="mb-4" />
        </TogglePanel>

        <!-- Metadata -->
        <TogglePanel heading="Metadata" id="box-search-metadata" expanded>
          <label class="label" for="createdBy">Created by</label>
          <v-select 
            v-model="createdBy" 
            placeholder="Select user..." 
            inputId="createdBy" 
            :options="users" 
            class="mb-4" 
          />
          <label class="label" for="lastModifiedBy">Last modified by</label>
          <v-select 
            v-model="lastModifiedBy" 
            placeholder="Select user..." 
            inputId="lastModifiedBy" 
            :options="users" 
            class="mb-4" 
          />
          <label class="label">Published date</label>
          <div class="columns is-mobile is-gapless is-vcentered mb-4">
            <div class="column"><input type="date" :min="minDate" :max="today" v-model="datePublishedFrom" @input="updateAll" /></div>
            <div class="column has-text-centered">to</div>
            <div class="column"><input type="date" :min="minDate" :max="today" v-model="datePublishedTo" @input="updateAll" /></div>
          </div>
          <label class="label">Created date</label>
          <div class="columns is-mobile is-gapless is-vcentered mb-4">
            <div class="column"><input type="date" :min="minDate" :max="today" v-model="dateCreatedFrom" @input="updateAll" /></div>
            <div class="column has-text-centered">to</div>
            <div class="column"><input type="date" :min="minDate" :max="today" v-model="dateCreatedTo" @input="updateAll" /></div>
          </div>
          <label class="label">Last modified date</label>
          <div class="columns is-mobile is-gapless is-vcentered mb-4">
            <div class="column"><input type="date" :min="minDate" :max="today" v-model="dateLastModifiedFrom" @input="updateAll" /></div>
            <div class="column has-text-centered">to</div>
            <div class="column"><input type="date" :min="minDate" :max="today" v-model="dateLastModifiedTo" @input="updateAll" /></div>
          </div>
        </TogglePanel>
        
      </div>

      <!-- MONOGRAPHS -->
      <div id="region-content" class="column is-two-thirds">
        
        <CreateNewMonograph v-if="isCreateMonographWindowOpen" @close="isCreateMonographWindowOpen = false" />
        
        <button type="button" id="btn-create-new-monograph" @click="isCreateMonographWindowOpen = true" class="button is-link is-outlined is-medium is-pulled-right">
          Create new monograph
        </button>
         
        <h2 class="title list-page-heading">
          <span id="number-monograph-list-heading"> {{ numberResultsListHeading }} </span> 
          <span id="text-monograph-list-heading" class="ml-2"> {{ searchResultsListHeading }}</span>
        </h2>
        
        
        <!-- Search pills -->
        <div v-if="Object.keys(getSearchPills).length" class="tags are-medium">
          <SearchPill
            v-for="(param, id) in getSearchPills"
            :key="`${id}Pill`"
            :param="param"
            :errors="errors[id]"
            @removeParameter="removeSearchParameter(id)" />
        </div>
        
        <!-- Loading error -->
        <MessageBlock v-if="searchResultsError" class="is-danger">
          <p>There was a problem fetching the monograph list. Try refreshing the page.</p>
        </MessageBlock>
        
        <!-- Monograph list -->
        <MonographResult v-for="monograph in searchResults" :key="monograph.articleId" :monograph="monograph" @update="updateByResult" />
        
      </div>
    </VForm>
  </div>
</template>

<script>
import { Form as VForm } from 'vee-validate';
import { CreateNewMonograph, MonographResult } from '@/components/merckindex/list';
import ListPageMixin from '@/mixins/shared/list-page-mixin';
import { useHead } from '@unhead/vue';
import { useMerckIndexSearchStore } from '@/store/modules/merckindex/search';

export default {
  name: 'MerckIndexList',
  setup() {
    const merckIndexSearchStore = useMerckIndexSearchStore();

    useHead({
      title: 'Merck Index',
    });

    return {
      merckIndexSearchStore,
    };
  },
  mixins: [ListPageMixin],
  data() {
    return {
      minDate: '2013-01-01',
      isCreateMonographWindowOpen: false,
    };
  },
  methods: {
    updateByResult(){
      this.updateAll()
    }
  },
  computed: {
    // USERS
    users: {
      get() {
        return this.merckIndexSearchStore.UserList;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'UserList',
          value,
        });
      },
    },
    // STATUS
    status: {
      get() {
        return this.merckIndexSearchStore.SearchParams.status.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.status.value',
          value,
        });
      },
    },
    // CONTAINER NAME
    containerName: {
      get() {
        return this.merckIndexSearchStore.SearchParams.containerName.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.containerName.value',
          value,
        });
      },
    },
    // CREATED BY
    createdBy: {
      get() {
        return this.merckIndexSearchStore.SearchParams.createdBy.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.createdBy.value',
          value,
        });
      },
    },
    // LAST MODIFIED BY
    lastModifiedBy: {
      get() {
        return this.merckIndexSearchStore.SearchParams.lastModifiedBy.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.lastModifiedBy.value',
          value,
        });
      },
    },
    // DATE PUBLISHED
    datePublishedFrom: {
      get() {
        return this.merckIndexSearchStore.SearchParams.datePublishedFrom.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.datePublishedFrom.value',
          value,
        });
      },
    },
    datePublishedTo: {
      get() {
        return this.merckIndexSearchStore.SearchParams.datePublishedTo.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.datePublishedTo.value',
          value,
        });
      },
    },
    // DATE CREATED
    dateCreatedFrom: {
      get() {
        return this.merckIndexSearchStore.SearchParams.dateCreatedFrom.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.dateCreatedFrom.value',
          value,
        });
      },
    },
    dateCreatedTo: {
      get() {
        return this.merckIndexSearchStore.SearchParams.dateCreatedTo.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.dateCreatedTo.value',
          value,
        });
      },
    },
    // DATE LAST MODIFIED
    dateLastModifiedFrom: {
      get() {
        return this.merckIndexSearchStore.SearchParams.dateLastModifiedFrom.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.dateLastModifiedFrom.value',
          value,
        });
      },
    },
    dateLastModifiedTo: {
      get() {
        return this.merckIndexSearchStore.SearchParams.dateLastModifiedTo.value;
      },
      set(value) {
        this.merckIndexSearchStore.updateField({
          path: 'SearchParams.dateLastModifiedTo.value',
          value,
        });
      },
    },
    getSearchPills() {
      return this.merckIndexSearchStore.getSearchPills;
    },
    getSearchQueryString() {
      return this.merckIndexSearchStore.getSearchQueryString;
    },
    getSearchQueryStringWithoutStatus() {
      return this.merckIndexSearchStore.getSearchQueryStringWithoutStatus;
    },
  },
  watch: {
    createdBy() {
      this.updateAll();
    },
    lastModifiedBy() {
      this.updateAll();
    },
  },
  components: {
    CreateNewMonograph,
    MonographResult,
    VForm
  },
};
</script>